<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { IncidentReportType } from '@roadfootage/api-client'
import { formatIncidentType, formatNumber } from '../../utils/format'
import { useBountySubmissionStore } from '../../stores/bounty-submission'
import { useWalletStore } from '../../stores/wallet'

const MIN_POINTS = 100
const MAX_POINTS = 10000
const MIN_DESCRIPTION_LENGTH = 10

const bountyStore = useBountySubmissionStore()
const walletStore = useWalletStore()
const { form } = storeToRefs(bountyStore)
const { pointsBalance } = storeToRefs(walletStore)

const errors = ref<string[]>([])

const incidentTypeOptions = [
  {
    label: formatIncidentType(IncidentReportType.TrafficAccident),
    value: IncidentReportType.TrafficAccident,
    icon: 'mdi-tow-truck'
  },
  {
    label: formatIncidentType(IncidentReportType.BadDriving),
    value: IncidentReportType.BadDriving,
    icon: 'mdi-alert-octagon'
  },
  {
    label: formatIncidentType(IncidentReportType.RoadRage),
    value: IncidentReportType.RoadRage,
    icon: 'mdi-emoticon-angry'
  },
  {
    label: formatIncidentType(IncidentReportType.LawEnforcement),
    value: IncidentReportType.LawEnforcement,
    icon: 'mdi-police-badge'
  }
]

// Keep slider and input in sync
const rewardInput = ref(String(form.value.rewardAmountPoints))
watch(
  () => form.value.rewardAmountPoints,
  (val) => {
    rewardInput.value = String(val)
  }
)
function onRewardInput(val: string | number | null) {
  const n = Number(val)
  if (!isNaN(n) && n >= MIN_POINTS && n <= MAX_POINTS) {
    form.value.rewardAmountPoints = n
  }
}

const balanceAfterPosting = computed(() => {
  if (pointsBalance.value === null) return null
  return pointsBalance.value - form.value.rewardAmountPoints
})

const rewardExceedsBalance = computed(
  () => pointsBalance.value !== null && form.value.rewardAmountPoints > pointsBalance.value
)

function validate(): boolean {
  errors.value = []
  if (!form.value.incidentType) {
    errors.value.push('Please select an incident type.')
  }
  if (form.value.requestDescription.length < MIN_DESCRIPTION_LENGTH) {
    errors.value.push(`Description must be at least ${MIN_DESCRIPTION_LENGTH} characters.`)
  }
  if (form.value.rewardAmountPoints < MIN_POINTS || form.value.rewardAmountPoints > MAX_POINTS) {
    errors.value.push(`Reward must be between ${MIN_POINTS} and ${MAX_POINTS} points.`)
  }
  if (rewardExceedsBalance.value) {
    errors.value.push('Reward cannot exceed your current points balance.')
  }
  return errors.value.length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="q-col-gutter-md column">
    <!-- Incident type -->
    <div>
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">What type of incident?</div>
      <p class="text-caption text-blue-grey-6 q-mb-sm">
        This helps contributors understand what to look for.
      </p>
      <div class="row q-col-gutter-sm">
        <div v-for="option in incidentTypeOptions" :key="option.value" class="col-6 col-sm-3">
          <q-card
            flat
            bordered
            clickable
            class="incident-type-card"
            :class="{ 'incident-type-selected': form.incidentType === option.value }"
            @click="form.incidentType = option.value"
          >
            <q-card-section class="text-center q-pa-md">
              <q-icon :name="option.icon" size="lg" class="q-mb-xs" />
              <div class="text-caption text-weight-bold">{{ option.label }}</div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </div>

    <!-- Description -->
    <div>
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Describe the incident</div>
      <p class="text-caption text-blue-grey-6 q-mb-sm">
        Describe what happened and any details that might help contributors identify relevant
        footage.
      </p>
      <q-input
        v-model="form.requestDescription"
        type="textarea"
        filled
        label="Description"
        :maxlength="2048"
        counter
        rows="4"
        autogrow
      />
    </div>

    <!-- Additional Information -->
    <div>
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Additional Information</div>
      <p class="text-caption text-blue-grey-6 q-mb-sm">
        Provide any additional details for moderators to consider when approving bounty responses.
      </p>
      <q-input
        v-model="form.additionalInformation"
        type="textarea"
        filled
        label="Additional Information (optional)"
        :maxlength="2048"
        counter
        rows="4"
        autogrow
      />
    </div>

    <!-- Reward -->
    <div>
      <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Set a reward</div>
      <p class="text-caption text-blue-grey-6 q-mb-sm">
        A higher reward attracts more attention. Credits are deducted when you post the bounty and
        returned if it expires without a match.
      </p>

      <div class="row q-col-gutter-md items-center q-mb-sm">
        <div class="col">
          <q-slider
            v-model="form.rewardAmountPoints"
            :min="MIN_POINTS"
            :max="MAX_POINTS"
            :step="100"
            color="blue-8"
            track-color="blue-grey-2"
            label
            :label-value="`${formatNumber(form.rewardAmountPoints)} pts`"
          />
        </div>
        <div class="col-auto" style="width: 120px">
          <q-input
            v-model="rewardInput"
            type="number"
            dense
            outlined
            suffix="pts"
            :min="MIN_POINTS"
            :max="MAX_POINTS"
            @update:model-value="onRewardInput"
          />
        </div>
      </div>

      <div class="text-caption text-blue-grey-6">
        <span
          >Your balance: {{ pointsBalance !== null ? formatNumber(pointsBalance) : '—' }} pts</span
        >
        <span v-if="balanceAfterPosting !== null" class="q-ml-md">
          After posting:
          <span :class="rewardExceedsBalance ? 'text-negative' : 'text-positive'">
            {{ formatNumber(balanceAfterPosting) }} pts
          </span>
        </span>
      </div>

      <q-banner v-if="rewardExceedsBalance" rounded class="bg-red-1 text-red-9 q-mt-sm">
        <template #avatar><q-icon name="mdi-alert-circle" color="red-7" /></template>
        The reward exceeds your current balance.
      </q-banner>
    </div>

    <!-- Validation errors -->
    <div>
      <q-banner v-for="err in errors" :key="err" rounded class="bg-red-1 text-red-9 q-mb-sm">
        <template #avatar><q-icon name="mdi-alert-circle" color="red-7" /></template>
        {{ err }}
      </q-banner>
    </div>
  </div>
</template>

<style scoped>
.incident-type-card {
  cursor: pointer;
  border: 2px solid var(--q-blue-grey-3);
  transition:
    border-color 0.15s,
    background-color 0.15s;
}

.incident-type-card:hover {
  border-color: var(--q-blue-8);
}

.incident-type-selected {
  border-color: var(--q-blue-8) !important;
  background-color: #e3f2fd;
}
</style>
